import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { IoChevronDown, IoChevronUp, IoTimeOutline } from 'react-icons/io5';
import { useContext, useEffect, useMemo, useState } from 'react';
import { date, now } from '../common/time';
import InvoiceDataContext from '../contexts/InvoiceDataContext';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import MerchantContext from '../contexts/MerchantContext';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import StepperContext from '../contexts/StepperContext';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import { useCaptcha } from '../hooks/useCaptcha';
import { usePayment } from '../hooks/usePayment';
import { getBrowserData } from '../helpers/threeds';
import type { RentalsPaymentsPortalRouterOutputs } from '../trpc/client';
import { invoiceReference } from '../types';
import CustomButton from './CustomButton';
import MerchantLogo from './MerchantLogo';
import PaymentForm from './PaymentForm';
import PaymentMethodSelection from './PaymentMethodSelection';
import PaymentSummary from './PaymentSummary';
import StepperRentals from './StepperRentals';
import PushSubscriptionSelector from './PushSubscriptionSelector';
import { SubscriptionManagementCard } from '../contexts/SubscriptionManagementContext';
import PalommaLogoWithText from './svg/PalommaLogoWithText';

interface InvoiceDetailCardProps {
  reference: string;
  customerName: string;
  subtotal: string;
  cardFee: number;
  cardFeePercentage: number;
  cardFeeFormatted: string;
  total: string;
  compact?: boolean;
}

const InvoiceDetailCard = ({
  reference,
  customerName,
  subtotal,
  cardFee,
  cardFeePercentage,
  cardFeeFormatted,
  total,
  compact,
}: InvoiceDetailCardProps) => {
  const fontSize = compact ? 'sm' : undefined;
  const spacing = compact ? 3 : 4;

  return (
    <VStack align="stretch" spacing={spacing}>
      <HStack justify="space-between">
        <Text fontSize={fontSize} color="gray.600" flexShrink={0}>
          Referencia
        </Text>
        <Text
          fontSize={fontSize}
          fontWeight="500"
          fontFamily="mono"
          noOfLines={compact ? 1 : undefined}
          textAlign="right"
        >
          {reference}
        </Text>
      </HStack>
      <HStack justify="space-between">
        <Text fontSize={fontSize} color="gray.600" flexShrink={0}>
          Cliente
        </Text>
        <Text fontSize={fontSize} fontWeight="500" noOfLines={compact ? 1 : undefined} textAlign="right">
          {customerName}
        </Text>
      </HStack>
      <Divider />
      <HStack justify="space-between">
        <Text fontSize={fontSize} fontWeight="600">
          Subtotal
        </Text>
        <Text fontSize={fontSize} fontWeight="600">
          {subtotal}
        </Text>
      </HStack>
      {cardFee > 0 && (
        <>
          <HStack justify="space-between">
            <Text fontSize={fontSize} color="gray.600">
              Cargo tarjeta ({cardFeePercentage}%)
            </Text>
            <Text fontSize={fontSize} color="gray.600">
              {cardFeeFormatted}
            </Text>
          </HStack>
          <Divider />
          <HStack justify="space-between">
            <Text fontSize={fontSize} fontWeight="700">
              Total
            </Text>
            <Text fontSize={fontSize} fontWeight="700">
              {total}
            </Text>
          </HStack>
        </>
      )}
    </VStack>
  );
};

interface InfoAlertProps {
  message: string;
  compact?: boolean;
}

const CardFeeAlert = ({ message, compact }: InfoAlertProps) => (
  <Alert
    status="info"
    variant="subtle"
    mt={compact ? 3 : 4}
    borderRadius="lg"
    bg="blue.50"
    border="1px"
    borderColor="blue.100"
  >
    <AlertIcon color="blue.500" boxSize={compact ? 4 : undefined} />
    <AlertDescription fontSize={compact ? 'xs' : 'sm'} color="gray.700">
      {message}
    </AlertDescription>
  </Alert>
);

interface CountdownAlertProps {
  countdownDisplay: string;
  compact?: boolean;
  maxW?: string | Record<string, string>;
}

const CountdownAlert = ({ countdownDisplay, compact, maxW }: CountdownAlertProps) => (
  <Alert
    status="warning"
    variant="subtle"
    mt={compact ? 3 : 4}
    borderRadius="lg"
    bg="orange.50"
    border="1px"
    borderColor="orange.200"
    maxW={maxW}
    mx={maxW ? 'auto' : undefined}
  >
    <AlertIcon color="orange.500" boxSize={compact ? 4 : undefined} />
    <AlertDescription fontSize={compact ? 'xs' : 'sm'} color="gray.700">
      Este enlace de pago expira en{' '}
      <Text as="span" fontWeight="700">
        {countdownDisplay}
      </Text>
    </AlertDescription>
  </Alert>
);

interface PaymentViewProps {
  invoice: RentalsPaymentsPortalRouterOutputs['invoices']['getApiInvoice']['invoice'];
}

const TALL_VIEWPORT_THRESHOLD = 750;

const PaymentView = ({ invoice }: PaymentViewProps) => {
  const { totalAmountToPay } = useContext(InvoicesToPayContext);
  const { cardFeePercentage } = useContext(InvoiceDataContext);
  const { merchant } = useContext(MerchantContext);
  const {
    showSubscriptionOffer,
    selectedSubscription,
    totalSubscriptionAmount,
    isLoading: isSubscriptionLoading,
  } = useContext(SubscriptionsContext);
  const formatCurrency = useCurrencyFormatter();
  const toast = useToast();
  const { isOpen: isSummaryOpen, onToggle: onSummaryToggle, onOpen: onSummaryOpen } = useDisclosure();

  useEffect(() => {
    if (window.innerHeight >= TALL_VIEWPORT_THRESHOLD) onSummaryOpen();
  }, [onSummaryOpen]);

  const { activeStepContext, paymentMethodContext, paymentInformationContext, resetStepper } =
    useContext(StepperContext);
  const { activeStep, setActiveStep } = activeStepContext;
  const { paymentMethod, setPaymentMethod } = paymentMethodContext;
  const { paymentInformation } = paymentInformationContext;

  const hasPalommaPlusStep = showSubscriptionOffer;
  const steps = hasPalommaPlusStep
    ? ['Palomma Plus', 'Método de pago', 'Datos de pago', 'Confirmación']
    : ['Método de pago', 'Datos de pago', 'Confirmación'];
  const paymentMethodStep = hasPalommaPlusStep ? 2 : 1;
  const paymentDataStep = hasPalommaPlusStep ? 3 : 2;
  const confirmationStep = hasPalommaPlusStep ? 4 : 3;

  useEffect(() => {
    resetStepper(1);
  }, [resetStepper]);

  const payObject = useMemo(() => {
    if (paymentMethod?.id === 'pse' && paymentInformation.pseBank)
      return { type: 'pse' as const, bank: paymentInformation.pseBank };
    if (paymentMethod?.id === 'nequiButton' && paymentInformation.phoneNumber)
      return { type: 'nequiButton' as const, phoneNumber: paymentInformation.phoneNumber };
    const browserData = getBrowserData();
    if (
      paymentMethod?.id === 'card' &&
      browserData &&
      paymentInformation.cardCVC &&
      paymentInformation.cardExpDate &&
      paymentInformation.cardNumber
    )
      return {
        type: 'card' as const,
        card: {
          cardNumber: paymentInformation.cardNumber,
          expirationMonth: paymentInformation.cardExpDate.substring(0, 2),
          expirationYear: paymentInformation.cardExpDate.slice(-2),
          cvv: paymentInformation.cardCVC,
        },
        browserData,
        installments: paymentInformation.installments,
      };
    if (paymentMethod?.id === 'breb') return { type: 'breb' as const };
    if (paymentMethod?.id === 'bancolombiaButton') return { type: 'bancolombiaButton' as const };
  }, [paymentMethod, paymentInformation]);

  const payerInfo = useMemo(() => {
    if (
      paymentInformation.name &&
      paymentInformation.documentType &&
      paymentInformation.documentNumber &&
      paymentInformation.email
    )
      return {
        name: paymentInformation.name,
        documentType: paymentInformation.documentType,
        documentNumber: paymentInformation.documentNumber,
        email: paymentInformation.email,
      };
  }, [paymentInformation]);

  const puntosColombia = useMemo(() => {
    if (paymentInformation.puntosColombiaDocumentType && paymentInformation.puntosColombiaDocumentNumber)
      return {
        documentType: paymentInformation.puntosColombiaDocumentType,
        documentNumber: paymentInformation.puntosColombiaDocumentNumber,
      };
  }, [paymentInformation.puntosColombiaDocumentNumber, paymentInformation.puntosColombiaDocumentType]);

  const [paymentSubmitted, setPaymentSubmitted] = useState(false);

  // The challenge runs on the confirmation step, the one that submits the
  // payment, so an interactive one lands next to the button it blocks.
  const captcha = useCaptcha({ enabled: activeStep >= confirmationStep });

  const { processPayment, loading } = usePayment(captcha, () => {
    setPaymentSubmitted(false);
    toast({
      title: 'Error generando pago',
      description:
        'Hubo un error al generar tu pago. Refresca la página e inténtalo de nuevo. Si el error persiste, comunícate con nosotros.',
      status: 'error',
      duration: 6000,
      isClosable: true,
    });
  });

  const nextStep = async () => {
    if (activeStep === confirmationStep && payObject && payerInfo) {
      setPaymentSubmitted(true);
      processPayment(payObject, payerInfo, 'link', puntosColombia);
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const previousStep = () => {
    if (activeStep === paymentMethodStep && paymentMethod) setPaymentMethod(undefined);
    if (activeStep > 1) setActiveStep(activeStep - 1);
  };

  const isButtonDisabled = useMemo(() => {
    if (hasPalommaPlusStep && activeStep === 1 && selectedSubscription === null) return true;
    if (activeStep === paymentMethodStep && !paymentMethod) return true;
    if (activeStep === paymentDataStep && !paymentInformation.okay) return true;
    if (activeStep === confirmationStep && captcha.status !== 'ready') return true;
    return false;
  }, [
    activeStep,
    captcha.status,
    confirmationStep,
    hasPalommaPlusStep,
    paymentDataStep,
    paymentInformation,
    paymentMethod,
    paymentMethodStep,
    selectedSubscription,
  ]);

  const [secondsLeft, setSecondsLeft] = useState<number | null>(() => {
    if (!invoice.expirationDate) return null;
    const diff = date(invoice.expirationDate).diff(now(), 'second');
    return diff > 0 ? diff : 0;
  });

  useEffect(() => {
    if (!invoice.expirationDate) return;
    const timer = setInterval(() => {
      const diff = date(invoice.expirationDate).diff(now(), 'second');
      setSecondsLeft(diff > 0 ? diff : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [invoice.expirationDate]);

  const paymentInProgress = !!(invoice.paymentDate || paymentSubmitted);
  const isExpired = secondsLeft === 0 && !paymentInProgress;
  const showCountdown = secondsLeft !== null && secondsLeft <= 600 && secondsLeft > 0 && !paymentInProgress;
  const countdownDisplay = showCountdown
    ? `${Math.floor(secondsLeft! / 60)}:${(secondsLeft! % 60).toString().padStart(2, '0')}`
    : '';

  useEffect(() => {
    if (!isExpired) return;
    const timeout = setTimeout(() => {
      window.location.href = invoice.redirectUrl || `/${invoice.rentalsMerchantId}?id=${invoice.id}`;
    }, 30_000);
    return () => clearTimeout(timeout);
  }, [isExpired, invoice.id, invoice.redirectUrl, invoice.rentalsMerchantId]);

  const subtotalAmount = totalAmountToPay + totalSubscriptionAmount;
  const cardFee = paymentMethod?.id === 'card' ? Math.round(subtotalAmount * (cardFeePercentage / 100)) : 0;
  const finalTotal = subtotalAmount + cardFee;

  const cardFeeMessage =
    merchant?.cardFeeMessage ||
    'El cargo adicional cubre comisiones bancarias y no representa ingresos para la inmobiliaria.';

  const detailCardProps = {
    reference: invoiceReference(invoice),
    customerName: invoice.customerName || invoice.customerDocumentNumber,
    subtotal: formatCurrency(subtotalAmount),
    cardFee,
    cardFeePercentage,
    cardFeeFormatted: formatCurrency(cardFee),
    total: formatCurrency(finalTotal),
  };

  return (
    <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} minH="100vh" overflow="hidden">
      <GridItem
        bg="gray.50"
        p={{ base: 6, md: 12 }}
        display={{ base: 'none', lg: 'flex' }}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        h="100vh"
        position="relative"
      >
        <Box maxW="400px" w="100%">
          <Box mb={8}>
            <MerchantLogo boxWidth={['120px', '150px', '150px']} boxHeight={['60px', '80px', '80px']} />
          </Box>

          <VStack align="start" spacing={1} mb={8}>
            <Text color="gray.600" fontSize="sm">
              {invoice.description || 'Pago'}
            </Text>
            <Heading size="2xl" fontWeight="500">
              {formatCurrency(finalTotal)}
            </Heading>
          </VStack>

          <Box bg="white" borderRadius="lg" p={6} shadow="sm" border="1px" borderColor="gray.200">
            <InvoiceDetailCard {...detailCardProps} />
          </Box>

          {cardFee > 0 && <CardFeeAlert message={cardFeeMessage} />}
          {showCountdown && <CountdownAlert countdownDisplay={countdownDisplay} />}
        </Box>

        <Box position="absolute" bottom={6}>
          <PalommaLogoWithText width="120px" />
        </Box>
      </GridItem>

      <GridItem bg="white" p={{ base: 6, md: 12 }} overflowY="auto" h="100vh" display="flex" flexDirection="column">
        <Box display={{ base: 'block', lg: 'none' }} mb={4} flexShrink={0}>
          <HStack justify="space-between" align="center" cursor="pointer" onClick={onSummaryToggle} py={2}>
            <HStack spacing={3}>
              <MerchantLogo boxWidth={['60px', '60px', '60px']} boxHeight={['30px', '30px', '30px']} />
              <Box>
                <Text fontSize="xs" color="gray.500">
                  {invoice.description || 'Pago'}
                </Text>
                <HStack spacing={2}>
                  <Text fontSize="lg" fontWeight="600">
                    {formatCurrency(finalTotal)}
                  </Text>
                  {showCountdown && !isSummaryOpen && (
                    <Text fontSize="xs" fontWeight="600" color="orange.500">
                      {countdownDisplay}
                    </Text>
                  )}
                </HStack>
              </Box>
            </HStack>
            <IconButton
              aria-label="Ver detalle"
              icon={<Icon as={isSummaryOpen ? IoChevronUp : IoChevronDown} />}
              variant="ghost"
              size="sm"
            />
          </HStack>

          <Collapse in={isSummaryOpen} animateOpacity>
            <Box bg="gray.50" borderRadius="lg" p={4} mt={2} border="1px" borderColor="gray.200">
              <InvoiceDetailCard {...detailCardProps} compact />
            </Box>

            {cardFee > 0 && <CardFeeAlert message={cardFeeMessage} compact />}
            {showCountdown && <CountdownAlert countdownDisplay={countdownDisplay} compact />}
          </Collapse>

          <HStack mt={4} spacing={3} align="center">
            <Divider />
            <PalommaLogoWithText width="60px" height="20px" />
            <Divider />
          </HStack>
        </Box>

        {isExpired ? (
          <VStack justify="center" spacing={5} maxW="480px" mx="auto" my="auto" textAlign="center">
            <Box p={4} bg="orange.50" borderRadius="full">
              <Icon as={IoTimeOutline} boxSize={12} color="orange.400" />
            </Box>
            <Heading size="lg" fontWeight="600" color="gray.800">
              Enlace de pago expirado
            </Heading>
            <Text fontSize="md" color="gray.600">
              El tiempo para realizar este pago ha finalizado.
            </Text>
            <Text fontSize="sm" color="gray.400">
              Serás redirigido en unos momentos...
            </Text>
          </VStack>
        ) : paymentInProgress ? (
          <VStack justify="center" spacing={6} maxW="480px" mx="auto" my="auto" textAlign="center">
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Heading size="lg" fontWeight="600" color="gray.800">
              Pago en proceso
            </Heading>
            <Text fontSize="md" color="gray.600" lineHeight="tall">
              {(paymentMethod?.id || invoice.paymentMethod) === 'nequiButton'
                ? 'Tu pago está en proceso. Complétalo en tu aplicación Nequi. Podemos tardarnos unos minutos en confirmarte.'
                : 'Tu pago está en proceso. Espera unos momentos mientras confirmamos tu pago.'}
            </Text>
            <Text fontSize="sm" color="gray.400">
              Cerrar esta ventana no afectará tu pago.
            </Text>
          </VStack>
        ) : (
          <VStack
            align="stretch"
            spacing={6}
            maxW="480px"
            mx="auto"
            mt={{ base: 6, lg: 'auto' }}
            mb={{ base: 0, lg: 'auto' }}
          >
            {isSubscriptionLoading ? (
              <Box height="320px" display="flex" alignItems="center" justifyContent="center">
                <Spinner size="xl" />
              </Box>
            ) : (
              <>
                <Box>
                  <StepperRentals
                    steps={steps}
                    activeStep={activeStep - 1}
                    nextStep={nextStep}
                    previousStep={previousStep}
                  />
                </Box>
                <SubscriptionManagementCard />

                {loading ? (
                  <Box height="320px" display="flex" alignItems="center" justifyContent="center">
                    <Spinner size="xl" />
                  </Box>
                ) : (
                  <Box>
                    {hasPalommaPlusStep && activeStep === 1 && <PushSubscriptionSelector />}
                    {activeStep === paymentMethodStep && <PaymentMethodSelection />}
                    {activeStep === paymentDataStep && <PaymentForm />}
                    {activeStep === confirmationStep && <PaymentSummary />}
                  </Box>
                )}
              </>
            )}

            {/* Turnstile only paints inside this box when it needs the visitor
            to do something; the rest of the time it takes no space. */}
            <Box ref={captcha.containerRef} width="100%" />

            {activeStep === confirmationStep && captcha.status === 'error' && (
              <Text fontSize="sm" color="red.500" textAlign="center">
                No pudimos verificar tu seguridad.{' '}
                <Button variant="link" fontSize="sm" colorScheme="red" onClick={captcha.reset}>
                  Reintentar
                </Button>
              </Text>
            )}

            {!isSubscriptionLoading && (
              <Box display="flex" justifyContent="center" pt={4}>
                <HStack width="100%" gap={2}>
                  {activeStep > 1 && (
                    <CustomButton onClick={previousStep} isDisabled={loading} width="50%">
                      REGRESAR
                    </CustomButton>
                  )}
                  <Tooltip
                    label={
                      activeStep === confirmationStep && captcha.status === 'solving'
                        ? 'Estamos verificando tu seguridad, esto toma un momento'
                        : undefined
                    }
                    hasArrow
                    placement="top"
                  >
                    <Box as="span" display="inline-block" width={activeStep > 1 ? '50%' : '100%'}>
                      <CustomButton
                        onClick={nextStep}
                        isDisabled={isButtonDisabled || activeStep === steps.length + 1 || loading}
                        width="100%"
                      >
                        {activeStep === confirmationStep ? 'CONFIRMAR' : 'CONTINUAR'}
                      </CustomButton>
                    </Box>
                  </Tooltip>
                </HStack>
              </Box>
            )}

            <Text fontSize="xs" color="gray.500" textAlign="center">
              Al continuar, aceptas nuestros términos de servicio y política de privacidad.
            </Text>
          </VStack>
        )}
      </GridItem>
    </Grid>
  );
};

export default PaymentView;
